import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Loader2, Bell, EyeOff, Layers, Pencil } from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import MenuBatchEditDialog from './MenuBatchEditDialog';
import OrderImpactAlert from './OrderImpactAlert';
import RecipientBreakdown from './RecipientBreakdown';

const prettyRange = (b) => {
  if (b.start_date === b.end_date) return b.start_date;
  return `${b.start_date} → ${b.end_date}`;
};

/** List menu batches (date-range groups) with edit / re-notify / unpublish actions. */
const MenuBatchesPanel = ({ open, onClose, onChanged, products = [] }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmNotify, setConfirmNotify] = useState(null); // batch
  const [recipientCount, setRecipientCount] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [impact, setImpact] = useState(null);
  const [impactBatch, setImpactBatch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBatches(await adminService.getMenuBatches());
    } catch {
      toast.error('Failed to load menu batches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const doUnpublish = async (batch) => {
    setBusyId(batch.id);
    try {
      await adminService.unpublishMenuBatch(batch.id);
      toast.success('Batch unpublished');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unpublish');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnpublish = async (batch) => {
    // Order-impact guard (fails open — never blocks the unpublish).
    setBusyId(batch.id);
    try {
      const result = await adminService.previewMenuOrderImpact({ batch_id_unpublish: batch.id });
      if (result.total_orders_affected > 0) {
        setImpact(result);
        setImpactBatch(batch);
        setBusyId(null);
        return;
      }
    } catch {
      // fail open
    }
    await doUnpublish(batch);
  };

  const openNotify = async (batch) => {
    try {
      const { recipient_count } = await adminService.getMenuRecipientsCount();
      setRecipientCount(recipient_count);
    } catch {
      setRecipientCount(null);
    }
    setConfirmNotify(batch);
  };

  const handleNotify = async () => {
    const batch = confirmNotify;
    setBusyId(batch.id);
    try {
      const res = await adminService.notifyMenuBatch(batch.id, true);
      toast.success(`Announcement sent — ${res.emails_sent} email(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setBusyId(null);
      setConfirmNotify(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="menu-batches-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-amber-600" /> Menu Batches
          </DialogTitle>
          <DialogDescription>Date-range menus you can re-announce or unpublish as a unit.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : batches.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center" data-testid="menu-batches-empty">No range menus yet.</p>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`menu-batch-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{b.title || 'Untitled menu'}</p>
                    {b.published ? (
                      <Badge className="bg-green-100 text-green-800 text-[10px]">Live</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Draft</Badge>
                    )}
                    <Badge className={`text-[10px] ${b.mode === 'exclusive' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {b.mode === 'exclusive' ? 'Excl' : 'Add'}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {prettyRange(b)} · {b.item_count} item{b.item_count === 1 ? '' : 's'}
                    {b.notified_at ? ' · announced' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setEditingBatch(b)}
                    disabled={busyId === b.id}
                    data-testid={`menu-batch-edit-${b.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => openNotify(b)}
                    disabled={busyId === b.id || !b.published}
                    data-testid={`menu-batch-notify-${b.id}`}
                  >
                    <Bell className="h-3.5 w-3.5 mr-1" /> {b.notified_at ? 'Re-notify' : 'Notify'}
                  </Button>
                  {b.published && (
                    <Button
                      variant="outline" size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleUnpublish(b)}
                      disabled={busyId === b.id}
                      data-testid={`menu-batch-unpublish-${b.id}`}
                    >
                      <EyeOff className="h-3.5 w-3.5 mr-1" /> Unpublish
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      <AlertDialog open={!!confirmNotify} onOpenChange={(o) => { if (!o) setConfirmNotify(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send menu announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              Email{' '}
              <RecipientBreakdown count={recipientCount} testId="batch-recipient-count" />{' '}
              that &ldquo;{confirmNotify?.title || 'this menu'}&rdquo; is live. Marketing
              recipients receive images and descriptions with no pricing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={handleNotify} data-testid="batch-confirm-notify-btn">
              Send Announcement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editingBatch && (
        <MenuBatchEditDialog
          open={!!editingBatch}
          batch={editingBatch}
          products={products}
          onClose={() => setEditingBatch(null)}
          onSaved={async () => { await load(); onChanged?.(); }}
        />
      )}

      <OrderImpactAlert
        open={!!impactBatch}
        onOpenChange={(o) => { if (!o) { setImpactBatch(null); setImpact(null); } }}
        impact={impact}
        confirmLabel="Unpublish anyway"
        onProceed={() => {
          const b = impactBatch;
          setImpactBatch(null);
          setImpact(null);
          doUnpublish(b);
        }}
      />
    </Dialog>
  );
};

export default MenuBatchesPanel;
